/**
 * Helpers for operating-hours UIs: API may return 24h ("09:00") or 12h ("08:00 AM");
 * dropdowns use fixed option lists (HH:mm in add-location modal, generated labels in profile).
 */

export function normalizeTimeWhitespace(value: string): string {
  return value.replace(/\u202f|\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Parse API/store time string to 24h "HH:mm". Returns null if unparseable. */
export function parseOperatingTimeToHHmm(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (s === '') return null;
  s = normalizeTimeWhitespace(s);

  const ampm = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const m = parseInt(ampm[2], 10);
    const ap = ampm[3].toUpperCase();
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    if (ap === 'PM' && h !== 12) h += 12;
    if (ap === 'AM' && h === 12) h = 0;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  const tf = s.match(/^(\d{1,2}):(\d{2})$/);
  if (tf) {
    const h = Math.min(23, Math.max(0, parseInt(tf[1], 10)));
    const m = Math.min(59, Math.max(0, parseInt(tf[2], 10)));
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  const d = new Date(`1970-01-01 ${s}`);
  if (!Number.isNaN(d.getTime())) {
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  return null;
}

/** Snap "HH:mm" to the nearest 30-minute boundary (dropdown slots). */
export function snapHHmmToHalfHour(hhmm: string | null): string | null {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  let total = h * 60 + m;
  total = Math.round(total / 30) * 30;
  const max = 24 * 60 - 30;
  if (total > max) total = max;
  if (total < 0) total = 0;
  const nh = Math.floor(total / 60) % 24;
  const nm = total % 60;
  return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
}

export function hhmmToTotalMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}
